import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  AlertAndNotificationCreateBody,
  AlertAndNotificationCreateResponse,
  AlertAndNotificationDetail,
  AlertAndNotificationListItem,
  AlertsAndNotificationsRepository,
  AssetExpression,
  BodyStandardNotification,
  ChatNotification,
  ChatNotificationCreateBody,
  CorrespondingAssetsParams,
  CorrespondingItem,
  CorrespondingTask,
  CorrespondingTasksParams,
  CorrespondingTasksPayload,
  EmailNotification,
  EmailNotificationCreateBody,
  ExpressionsListParams,
  GetDetailEmailNotificationParams,
  GetListEmailNotificationParams,
  GetNonVesselSectorListParams,
  NonVesselSector,
  NotificationConfigTypeEnum,
  StandardNotification,
  TaskExpression,
} from '@app/domain';
import { PaginatedResponse } from '@app/shared/models/global.model';
import { AlertsAndNotificationsService } from '../api-clients/alerts-and-notifications.service';
import { AlertsAndNotificationsMapper } from '../mappers/alerts-and-notifications.mapper';

@Injectable()
export class AlertsAndNotificationsRepositoryImpl
  implements AlertsAndNotificationsRepository
{
  private readonly _alertsAndNotificationsMapper =
    new AlertsAndNotificationsMapper();

  constructor(
    private readonly _alertsAndNotificationsService: AlertsAndNotificationsService
  ) {}
  getAlertsAndNotifications(): Observable<AlertAndNotificationListItem[]> {
    const alertsAndNotificationsDTO =
      this._alertsAndNotificationsService.getAlertsAndNotifications();
    return alertsAndNotificationsDTO.pipe(
      map(dto =>
        this._alertsAndNotificationsMapper.alertsAndNotificationsToDomain(dto)
      )
    );
  }

  getAlertAndNotificationDetails(
    id: number
  ): Observable<AlertAndNotificationDetail> {
    const alertAndNotificationDetails =
      this._alertsAndNotificationsService.getAlertAndNotificationDetails(id);
    return alertAndNotificationDetails.pipe(
      map(dto =>
        this._alertsAndNotificationsMapper.alertAndNotificationDetailsToDomain(
          dto
        )
      )
    );
  }

  createAlertAndNotification(
    body: AlertAndNotificationCreateBody
  ): Observable<AlertAndNotificationCreateResponse> {
    const responseDto =
      this._alertsAndNotificationsService.createAlertAndNotification(body);
    return responseDto.pipe(
      map(dto => this._alertsAndNotificationsMapper.createResponseToDomain(dto))
    );
  }

  editAlertAndNotification(
    id: number,
    body: AlertAndNotificationCreateBody
  ): Observable<AlertAndNotificationCreateResponse> {
    const responseDto =
      this._alertsAndNotificationsService.editAlertAndNotification(id, body);
    return responseDto.pipe(
      map(dto => this._alertsAndNotificationsMapper.createResponseToDomain(dto))
    );
  }

  deleteAlertAndNotification(id: number): Observable<void> {
    return this._alertsAndNotificationsService.deleteAlertAndNotification(id);
  }

  getCorrespondingTasks(
    params: CorrespondingTasksParams,
    payload: CorrespondingTasksPayload
  ): Observable<PaginatedResponse<CorrespondingTask>> {
    const listDto = this._alertsAndNotificationsService.getCorrespondingTasks(
      params,
      payload
    );
    return listDto.pipe(
      map(dto =>
        this._alertsAndNotificationsMapper.correspondingTasksToDomain(dto)
      )
    );
  }

  deleteCorrespondingTasksFromConfig(
    id: number,
    taskId: number
  ): Observable<void> {
    return this._alertsAndNotificationsService.deleteCorrespondingTasksFromConfig(
      id,
      taskId
    );
  }

  getListExpressions(
    assetId: number,
    params: ExpressionsListParams
  ): Observable<AssetExpression> {
    const listExpressionDTO =
      this._alertsAndNotificationsService.getListExpressions(assetId, params);
    return listExpressionDTO.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.expressionListToDomain(dtos)
      )
    );
  }

  createEmailNotifications(
    body: EmailNotificationCreateBody
  ): Observable<void> {
    return this._alertsAndNotificationsService.createEmailNotifications(body);
  }

  getListEmailNotifications(
    params: GetListEmailNotificationParams
  ): Observable<EmailNotification[]> {
    const emailNotificationDtos =
      this._alertsAndNotificationsService.getListEmailNotifications(params);
    return emailNotificationDtos.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.emailNotificationListToDomain(dtos)
      )
    );
  }

  getCorrespondingAssets(
    params: CorrespondingAssetsParams
  ): Observable<PaginatedResponse<CorrespondingItem>> {
    const correspondingAssetDtos =
      this._alertsAndNotificationsService.getCorrespondingAssets(params);

    return correspondingAssetDtos.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.correspondingAssetsToDomain(dtos)
      )
    );
  }

  deleteEmailNotifications(id: number): Observable<void> {
    return this._alertsAndNotificationsService.deleteEmailNotifications(id);
  }

  getEmailNotificationDetails(id: number): Observable<EmailNotification> {
    const params = {
      id,
      notificationConfigType: NotificationConfigTypeEnum.ALERT_MANAGEMENT,
    };

    const emailNotificationDtos =
      this._alertsAndNotificationsService.getEmailNotificationDetails(params);

    return emailNotificationDtos.pipe(
      map(dtos => {
        if (dtos.length) {
          const [dto] = dtos;
          return this._alertsAndNotificationsMapper.emailNotificationToDomain(
            dto
          );
        }
        return {} as EmailNotification;
      })
    );
  }

  getTaskTemplateExpression(
    taskId?: number,
    subTaskId?: number
  ): Observable<TaskExpression> {
    const taskExpressionDTO =
      this._alertsAndNotificationsService.getTaskTemplateExpression(
        taskId,
        subTaskId
      );
    return taskExpressionDTO.pipe(
      map(dto => this._alertsAndNotificationsMapper.taskExpressionToDomain(dto))
    );
  }

  getStandardNotification(): Observable<StandardNotification[]> {
    const standardNotificationDTOs =
      this._alertsAndNotificationsService.getStandardNotification();
    return standardNotificationDTOs.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.standardNotificationListToDomain(
          dtos
        )
      )
    );
  }

  editStandardNotification(body: BodyStandardNotification[]): Observable<void> {
    return this._alertsAndNotificationsService.editStandardNotification(body);
  }

  getChatNotificationDetails(id: number): Observable<ChatNotification> {
    const params: GetDetailEmailNotificationParams = {
      id,
      notificationConfigType: NotificationConfigTypeEnum.PUBLISH_CHAT,
    };

    const emailNotificationDtos =
      this._alertsAndNotificationsService.getChatNotificationDetails(params);

    return emailNotificationDtos.pipe(
      map(dtos => {
        if (dtos.length) {
          const [dto] = dtos;

          return this._alertsAndNotificationsMapper.chatNotificationToDomain(
            dto
          );
        }
        return {} as ChatNotification;
      })
    );
  }

  createChatNotifications(body: ChatNotificationCreateBody): Observable<void> {
    return this._alertsAndNotificationsService.createChatNotifications(body);
  }

  deleteChatNotifications(id: number): Observable<void> {
    return this._alertsAndNotificationsService.deleteChatNotifications(id);
  }

  getListChatNotifications(
    params: GetListEmailNotificationParams
  ): Observable<ChatNotification[]> {
    const emailNotificationDtos =
      this._alertsAndNotificationsService.getListChatNotifications(params);
    return emailNotificationDtos.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.chatNotificationListToDomain(dtos)
      )
    );
  }

  getNonVesselSectorList(
    params: GetNonVesselSectorListParams
  ): Observable<NonVesselSector[]> {
    const emailNotificationDtos =
      this._alertsAndNotificationsService.getNonVesselSectorList(params);
    return emailNotificationDtos.pipe(
      map(dtos =>
        this._alertsAndNotificationsMapper.nonVesselSectorListToDomain(
          dtos.data
        )
      )
    );
  }
}
